import { useState, useEffect, useCallback } from "react";

/**
 * Интерфейс за BeforeInstallPromptEvent — не е стандартен DOM тип,
 * затова го дефинираме ръчно за TypeScript поддръжка.
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
  prompt(): Promise<void>;
}

/** Резултат от useInstallPrompt hook-а */
interface UseInstallPromptResult {
  /** Дали install prompt-ът е наличен и трябва да се покаже UI */
  isInstallable: boolean;
  /** Дали app-ът вече е отворен в standalone/PWA режим */
  isStandalone: boolean;
  /** Извиква native install prompt-а на браузъра */
  promptInstall: () => Promise<void>;
  /** Ръчно скрива install UI-а (dismiss бутон) */
  dismiss: () => void;
}

/**
 * Проверява дали app-ът работи в standalone/PWA режим.
 * Покрива и CSS display-mode media query, и iOS Safari navigator.standalone.
 */
function getIsStandalone(): boolean {
  // CSS display-mode: standalone — работи на Chrome, Edge, Firefox
  const mediaMatch = window.matchMedia("(display-mode: standalone)").matches;

  // iOS Safari специфична проверка
  const iosStandalone = (navigator as unknown as { standalone?: boolean })
    .standalone;

  return mediaMatch || iosStandalone === true;
}

/**
 * Hook за управление на PWA install prompt-а.
 * Прихваща beforeinstallprompt event-а, показва custom UI,
 * и скрива всичко ако app-ът е вече инсталиран или в standalone mode.
 */
export function useInstallPrompt(): UseInstallPromptResult {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Провери дали е standalone при mount
    const standalone = getIsStandalone();
    setIsStandalone(standalone);

    // Ако вече е standalone/PWA — няма смисъл да слушаме за install prompt
    if (standalone) return;

    /**
     * beforeinstallprompt се пуска от браузъра когато app-ът отговаря
     * на критериите за инсталация. Предотвратяваме default поведението
     * (автоматичен prompt) и запазваме event-а за по-късно използване.
     */
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    /**
     * appinstalled event — браузърът потвърждава успешна инсталация.
     * Скриваме install UI-а.
     */
    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setIsInstallable(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleAppInstalled);

    // Слушай за промяна на display-mode (ако потребителят отвори PWA)
    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    const handleDisplayChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        setIsStandalone(true);
        setIsInstallable(false);
      }
    };
    mediaQuery.addEventListener("change", handleDisplayChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleAppInstalled);
      mediaQuery.removeEventListener("change", handleDisplayChange);
    };
  }, []);

  /**
   * Извиква native install prompt-а на браузъра.
   * След избора на потребителя (accept/dismiss) скриваме UI-а.
   */
  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    // Изчакваме потребителският избор
    await deferredPrompt.userChoice;
    // Независимо от резултата — скриваме prompt UI-а
    setDeferredPrompt(null);
    setIsInstallable(false);
  }, [deferredPrompt]);

  /** Ръчно dismiss — потребителят затваря banner-а без да инсталира */
  const dismiss = useCallback(() => {
    setIsInstallable(false);
    setDeferredPrompt(null);
  }, []);

  return { isInstallable, isStandalone, promptInstall, dismiss };
}
